import { useCallback, useEffect, useState } from 'react';
import { Search, Warehouse, AlertTriangle } from 'lucide-react';
import { API_URL } from '../config';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Per-product stock across every bodega. Flags bodegas with no stock when
 * another bodega still has units (e.g. empty Sala de ventas, stock in Almacenamiento).
 */
export default function StockPorBodega({ onTransfer = null, transferVersion = 0 }) {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [detail, setDetail] = useState(null); // { producto, bodegas }
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const q = query.trim();
        if (!q) {
            setSuggestions([]);
            return undefined;
        }
        let cancelled = false;
        const timeoutId = setTimeout(async () => {
            try {
                const res = await fetch(`${API_URL}/api/stock/productos?search=${encodeURIComponent(q)}`, { headers: authHeaders() });
                const data = await res.json().catch(() => []);
                if (!cancelled) setSuggestions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                if (!cancelled) setSuggestions([]);
            }
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [query]);

    const loadProduct = useCallback(async (id) => {
        setLoading(true);
        setError('');
        setSuggestions([]);
        try {
            const res = await fetch(`${API_URL}/api/stock/product/${id}`, { headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'No se pudo cargar el stock del producto.');
            setDetail(data);
            setQuery('');
        } catch (err) {
            setDetail(null);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Refresh the open product after a transfer made from this tab.
    const openProductId = detail?.producto?.id;
    useEffect(() => {
        if (transferVersion > 0 && openProductId) loadProduct(openProductId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transferVersion]);

    // A barcode scanner types the code and presses Enter: open the exact match, or the only result.
    const handleSubmit = (e) => {
        e.preventDefault();
        const q = query.trim();
        const exact = suggestions.find((s) => s.codigo_barra === q) || (suggestions.length === 1 ? suggestions[0] : null);
        if (exact) loadProduct(exact.id);
    };

    const bodegas = detail?.bodegas || [];
    const total = bodegas.reduce((acc, b) => acc + b.cantidad, 0);
    const withStock = bodegas.filter((b) => b.cantidad > 0);
    const empty = bodegas.filter((b) => b.cantidad <= 0);

    return (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-6">
            <form onSubmit={handleSubmit} className="relative max-w-xl mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Escanee o busque un producto por nombre o código..."
                    className="w-full pl-14 pr-4 py-2 border rounded-lg"
                />
                {suggestions.length > 0 && (
                    <ul className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow max-h-72 overflow-y-auto">
                        {suggestions.map((s) => (
                            <li key={s.id}>
                                <button type="button" onClick={() => loadProduct(s.id)} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                                    <div className="font-medium">{s.nombre}</div>
                                    <div className="text-xs text-gray-500">{s.codigo_barra}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </form>

            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            {loading && <p className="text-gray-500 text-sm">Cargando...</p>}

            {detail && !loading && (
                <>
                    <div className="mb-3">
                        <h3 className="font-bold text-lg">{detail.producto.nombre}</h3>
                        <p className="text-sm text-gray-500">{detail.producto.codigo_barra} · Total en todas las bodegas: <strong>{total}</strong></p>
                    </div>

                    {withStock.length > 0 && empty.length > 0 && (
                        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex gap-2">
                            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                            <div>
                                Sin stock en {empty.map((b) => b.bodega_nombre).join(', ')}.
                                Hay en {withStock.map((b) => `${b.bodega_nombre} (${b.cantidad})`).join(', ')}.
                                Puede reponer con una transferencia desde la pestaña «Stock actual».
                            </div>
                        </div>
                    )}

                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="p-3 text-sm">Sucursal</th>
                                <th className="p-3 text-sm">Bodega</th>
                                <th className="p-3 text-sm text-right">Stock</th>
                                {onTransfer && <th className="p-3 w-32" />}
                            </tr>
                        </thead>
                        <tbody>
                            {bodegas.map((b) => (
                                <tr key={b.bodega_id} className={`border-b ${b.cantidad <= 0 ? 'bg-red-50 text-red-700' : ''}`}>
                                    <td className="p-3">{b.sucursal_nombre || '—'}</td>
                                    <td className="p-3"><span className="inline-flex items-center gap-2"><Warehouse size={14} />{b.bodega_nombre}</span></td>
                                    <td className="p-3 text-right font-mono font-bold">{b.cantidad}</td>
                                    {onTransfer && (
                                        <td className="p-3 text-right">
                                            {b.cantidad > 0 && (
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => onTransfer({
                                                        producto_id: detail.producto.id,
                                                        producto_nombre: detail.producto.nombre,
                                                        sucursal_id: b.sucursal_id,
                                                        sucursal_nombre: b.sucursal_nombre,
                                                        bodega_id: b.bodega_id,
                                                        bodega_nombre: b.bodega_nombre,
                                                        cantidad: b.cantidad,
                                                    })}
                                                >
                                                    Transferir
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {!detail && !loading && !error && (
                <p className="text-gray-500 text-sm">Busque un producto para ver cuánto stock tiene en cada bodega.</p>
            )}
        </div>
    );
}
